import { CaretDown, Check, Copy, Info, SignIn, X } from "@phosphor-icons/react";
import {
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuProvider,
  Tooltip,
  TooltipAnchor,
  TooltipArrow,
  TooltipProvider,
} from "@replicate/ui";
import { useMutation } from "@tanstack/react-query";
import copyToClipboard from "copy-to-clipboard";
import pluralize from "pluralize";
import { useState } from "react";
import toast from "react-hot-toast";
import { match } from "ts-pattern";
import type { Prediction } from "../../types";
import { route } from "../../urls";
import ConditionalWrap from "../conditional-wrap";
import { sharePrediction, sharePredictionWithChildren } from "./api";
import { usePlaygroundContext } from "./context";

export function SharePrediction({
  prediction,
}: {
  prediction: Prediction;
}) {
  const { isAuthenticated } = usePlaygroundContext();

  if (!isAuthenticated) {
    return (
      <Button
        variant="outlined"
        render={
          // biome-ignore lint/a11y/useAnchorContent: Content is injected by the component.
          <a href={`${route("signin")}?next=${window.location.href}`} />
        }
        startIcon={<SignIn size={16} />}
      >
        <span>Share</span>
      </Button>
    );
  }

  const [hasIncludeChildren, setIncludeChildren] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isImmutable = prediction._extras.is_immutable;
  const isShared = prediction._extras.is_shared;
  const totalChildPredictions = prediction._extras.total_child_predictions;
  const hasChildPredictions = totalChildPredictions > 0;

  const shareMutation = useMutation({
    mutationFn: ({ includeChildren }: { includeChildren: boolean }) =>
      includeChildren
        ? sharePredictionWithChildren({ id: prediction.id })
        : sharePrediction({ id: prediction.id }),
    onSuccess: () => {
      prediction._extras.is_shared = true;
      const predictionPath = route("prediction_detail", {
        prediction_uuid: prediction.id,
      });
      const url = `${window.location.origin}${predictionPath}`;
      copyToClipboard(url);
      setTimeout(() => {
        shareMutation.reset();
        setIsMenuOpen(false);
      }, 1000);
    },
    onError: (e) => {
      let errorMessage = "Sharing prediction failed";
      if (e instanceof Error) {
        errorMessage += `: ${e.message}`;
      }
      toast.error(errorMessage);
    },
  });

  const parentButtonIcon = <Copy size={16} />;
  const parentButtonText = "Share";

  const isLoading = shareMutation.status === "pending";
  const hasSuccess = shareMutation.status === "success";
  const hasError = shareMutation.status === "error";

  const innerButtonIcon = match({ isLoading, hasSuccess, hasError })
    .with({ isLoading: true }, () => null)
    .with({ hasSuccess: true }, () => <Check size={16} />)
    .with({ hasError: true }, () => <X size={16} />)
    .otherwise(() => (isShared ? <Copy size={14} /> : null));

  const innerButtonText = match({ isLoading, hasSuccess, hasError })
    .with({ isLoading: true }, () => (isShared ? "Copying..." : "Sharing..."))
    .with({ hasSuccess: true }, () => "Copied URL to clipboard")
    .with({ hasError: true }, () =>
      isShared ? "Copying failed" : "Sharing failed"
    )
    .otherwise(() => (isShared ? "Copy prediction URL" : "Share prediction"));

  return (
    <ConditionalWrap
      condition={isImmutable}
      wrap={(children) => {
        return (
          <TooltipProvider>
            <TooltipAnchor render={<div>{children}</div>} />
            <Tooltip className="max-w-xs text-center">
              <TooltipArrow />
              Sharing is disabled because this prediction has been automatically
              archived.
            </Tooltip>
          </TooltipProvider>
        );
      }}
    >
      <MenuProvider open={isMenuOpen} setOpen={setIsMenuOpen}>
        <MenuButton
          disabled={isImmutable}
          style={{
            pointerEvents: "auto",
            borderRadius: "0",
          }}
          render={
            <Button
              variant="outlined"
              startIcon={parentButtonIcon}
              endIcon={<CaretDown size={16} />}
            />
          }
        >
          <span>{parentButtonText}</span>
        </MenuButton>
        <Menu className="w-80" gutter={8}>
          <div className="flex items-start gap-2 text-r8-sm text-r8-gray-11 p-4 border-b border-r8-gray-5 bg-r8-gray-2">
            <Info size={16} className="mt-0.5 flex-shrink-0" />
            {isShared ? (
              <span>
                This prediction has already been shared and is publicly
                accessible.
              </span>
            ) : (
              <span>
                Sharing a prediction makes it publicly accessible. This cannot
                be undone.
              </span>
            )}
          </div>

          <div className="p-4 space-y-4">
            {hasChildPredictions && !isShared && (
              <div className="flex items-center gap-2">
                <Checkbox
                  type="checkbox"
                  checked={hasIncludeChildren}
                  onChange={(e) => setIncludeChildren(e.target.checked)}
                  disabled={isLoading}
                />
                <label className="text-r8-sm cursor-pointer">
                  Also share {totalChildPredictions}{" "}
                  {pluralize("step", totalChildPredictions)} used to create this
                  prediction.
                </label>
              </div>
            )}

            <Button
              size="sm"
              variant="filled"
              className="w-full"
              loading={isLoading}
              disabled={isLoading}
              onClick={() => {
                shareMutation.mutate({
                  includeChildren: hasChildPredictions && hasIncludeChildren,
                });
              }}
            >
              {innerButtonText}
            </Button>
          </div>
        </Menu>
      </MenuProvider>
    </ConditionalWrap>
  );
}
